"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-border pt-10">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">
          [SEC_03] — TRANSMISSION SUCCESSFUL
        </p>
        <h3 className="font-default text-3xl md:text-4xl text-foreground font-bold mb-4">
          Inquiry Logged.
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Log New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              FULL NAME / CONTACT PERSON
            </FormLabel>
            <FormControl>
              <Input placeholder="CHARLES SMITH" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              CORPORATE EMAIL
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="charles@imcncdesigns.co.za" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2" />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              CONTACT NUMBER
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="082 553 8657" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary transition-colors rounded-md" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2" />
          </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem className="gap-0">
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              TECHNICAL SPECIFICATIONS / SPARE PARTS REQUIRED
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Please list Siemens part numbers, machine model, or specific retrofitting requirements." {...field} className="min-h-[160px] border-0 border-b border-border bg-transparent px-0 py-4 text-xl text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary transition-colors resize-none rounded-md" />
            </FormControl>
            <FormMessage className="font-mono text-xs mt-2" />
          </FormItem>} />

      {submitError && <div className="border-l-2 border-destructive pl-4 py-2">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">
            {submitError}
          </p>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-6">
        <div className="flex items-start gap-3 max-w-sm">
          <Checkbox id="consent" className="mt-1 rounded-md border-primary" />
          <label htmlFor="consent" className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground leading-relaxed cursor-pointer">
            I authorize IMCNC Designs to process this technical inquiry. Data handled per industrial privacy standards.
          </label>
        </div>

        <Button type="submit" disabled={isSubmitting} className="font-mono text-xs uppercase tracking-[0.2em] bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-8 py-6 h-auto group">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing
            </> : <>
              Submit Inquiry
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ContactInquiryForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2569" id="contact-inquiry-form" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <h2 className="font-default text-5xl md:text-6xl text-foreground font-bold leading-[1.05] mb-8">
              Contact an Expert.
            </h2>

            <p className="text-base text-muted-foreground leading-relaxed mb-12 max-w-md font-default">
              Our engineering team responds to all technical inquiries within 24 hours. 
              Please provide Siemens part numbers or machine specifications to expedite 
              your retrofitting or spare parts request.
            </p>

            <div className="border-t border-border pt-8 space-y-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">
                DIRECT CHANNELS
              </p>

              <div className="items-start gap-3 flex">
                <span className="text-primary font-mono text-xs mt-1">+</span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    PHONE
                  </p>
                  <p className="text-foreground text-base font-default">+27 82 553 8657</p>
                </div>
              </div>

              <div className="items-start gap-3 flex">
                <span className="text-primary font-mono text-xs mt-1">+</span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    EMAIL
                  </p>
                  <p className="text-foreground text-base font-default">charles@imcncdesigns.co.za</p>
                </div>
              </div>

              <div className="items-start gap-3 flex">
                <span className="text-primary font-mono text-xs mt-1">+</span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    HEADQUARTERS
                  </p>
                  <p className="text-foreground text-base leading-relaxed font-default">
                    Wilge rd., Golfpark, Gauteng
                    <br />
                    <span className="text-muted-foreground text-sm">South Africa, 1961</span>
                  </p>
                </div>
              </div>

              <div className="items-start gap-3 flex">
                <span className="text-primary font-mono text-xs mt-1">+</span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    OPERATIONS
                  </p>
                  <p className="text-foreground text-base font-default">Mon–Fri: 08:00 – 17:00</p>
                  <p className="text-foreground text-base font-default">Emergency Support: 24/7</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="contact_inquiry_form_contact_us" formId="contact_inquiry_form_contact_us" sectionName="contact-inquiry-form" defaultValues={{
            name: "",
            email: "",
            phone: "",
            details: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            }
          }} submitText="Submit Inquiry" successMessage="Your technical inquiry has been logged. A Siemens specialist will contact you within one business day to discuss your requirements." errorMessage="Transmission error. Please verify your connection or contact us via phone.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}